/** Types, defaults and helpers shared by the optimization store slices (AIT-423). */
export type WorkerAgentConfig = {
  /** Backend agent id (UUID or legacy numeric string like "1") */
  id: string;
  name: string;
  prompt: string;
  model: string;
  temperature: number;
  description: string;
  mode?: 'synchronous' | 'asynchronous';
  reminder_prompt?: string;
};

export type AdditionalWorkerConfig = {
  name: string;
  prompt: string;
  model: string;
  temperature: number;
  description: string;
};

export type MultiAgentDefaults = {
  selectedDataset: string;
  multiAgentIterations: number;
  coordinatorPrompt: string;
  coordinatorModel: string;
  coordinatorTemperature: number;
  coordinatorDescription: string;
  coordinatorReminderPrompt: string;
  /** Display name for coordinator (master-agent) in UI */
  coordinatorName: string;
  workers: WorkerAgentConfig[];
  additionalWorkerConfig: AdditionalWorkerConfig;
};

// Интерфейс для сообщений чата
export interface ChatMessage {
  id: number;
  text: string;
  sender: 'user' | 'bot';
  intermediateMessages?: Array<{
    type: 'ai';
    content: string;
    name: string;
    id: string;
  }>;
}

export const cloneWorkers = (workers: WorkerAgentConfig[]): WorkerAgentConfig[] =>
  workers.map((worker) => ({ ...worker }));

export const cloneAdditionalWorkerConfig = (config: AdditionalWorkerConfig): AdditionalWorkerConfig => ({
  ...config,
});

export const areWorkersEqual = (a: WorkerAgentConfig[], b: WorkerAgentConfig[]): boolean => {
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i += 1) {
    const workerA = a[i];
    const workerB = b[i];
    if (!workerB) return false;
    if (
      String(workerA.id) !== String(workerB.id) ||
      workerA.name !== workerB.name ||
      workerA.prompt !== workerB.prompt ||
      workerA.model !== workerB.model ||
      workerA.temperature !== workerB.temperature ||
      workerA.description !== workerB.description ||
      (workerA.reminder_prompt || '') !== (workerB.reminder_prompt || '')
    ) {
      return false;
    }
  }
  return true;
};

export const areAdditionalWorkerConfigsEqual = (a: AdditionalWorkerConfig, b: AdditionalWorkerConfig): boolean =>
  a.name === b.name &&
  a.prompt === b.prompt &&
  a.model === b.model &&
  a.temperature === b.temperature &&
  a.description === b.description;

export const DEFAULT_COORDINATOR_DESCRIPTION =
  'Coordinator node that communicates with worker nodes to respond to user messages.';

export const INITIAL_WORKERS: WorkerAgentConfig[] = [
  {
    id: '1',
    name: 'Expert',
    prompt: 'You are a helpful assistant.',
    model: 'gpt-4.1-mini',
    temperature: 0.7,
    description: 'AI agent that follows this prompt:\nYou are a helpful assistant.',
  },
  {
    id: '2',
    name: 'Synthesizer',
    prompt: 'You are a helpful assistant.',
    model: 'gpt-4.1-mini',
    temperature: 0.7,
    description: 'AI agent that follows this prompt:\nYou are a helpful assistant.',
  },
];

export const INITIAL_ADDITIONAL_WORKER_CONFIG: AdditionalWorkerConfig = {
  name: 'Assistant',
  prompt: 'You are a helpful assistant',
  model: 'gpt-4.1-mini',
  temperature: 0.7,
  description: 'General-purpose assistant',
};
